import { DeleteOutline, EditOutlined } from "@mui/icons-material";
import {
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    TextField,
} from "@mui/material";
import { GridRenderCellParams } from "@mui/x-data-grid-pro";
import AcxButton from "components/UI/AcxButton/AcxButton";
import AcxDataGrid from "components/UI/AcxDataGrid/AcxDataGrid";
import AcxDataGridStore from "components/UI/AcxDataGrid/AcxDataGridStore";
import IColDef from "components/UI/AcxDataGrid/IColDef";
import AcxMainTextField from "components/UI/AcxMainTextField";
import { observer } from "mobx-react";
import { ProgramEvent } from "models/ClientBrain/OrganizationContext";
import React, { useEffect, useState } from "react";
import ClientBrainStore from "stores/ClientBrainStore";
import ClientBrainSectionCard from "../ClientBrainSectionCard";
import { formatShortDate, gridHeight } from "../clientBrainUtils";

type Props = {
    store: ClientBrainStore;
    canEdit?: boolean;
};

/** yyyy-mm-dd for the native date input, from an ISO string. */
function toDateInputValue(iso?: string): string {
    if (!iso) return "";
    const match = /^(\d{4}-\d{2}-\d{2})/.exec(iso);
    return match ? match[1] : "";
}

const ProgramEventsSection: React.FC<Props> = observer(
    ({ store, canEdit = false }) => {
        const [dgStore] = useState(
            () => new AcxDataGridStore("client-brain-program-events"),
        );
        const [open, setOpen] = useState(false);
        const [editingId, setEditingId] = useState<string | null>(null);
        const [date, setDate] = useState("");
        const [description, setDescription] = useState("");
        const [saving, setSaving] = useState(false);

        const openAdd = () => {
            setEditingId(null);
            setDate("");
            setDescription("");
            setOpen(true);
        };

        const openEdit = (event: ProgramEvent) => {
            setEditingId(event.id);
            setDate(toDateInputValue(event.startDate));
            setDescription(event.description);
            setOpen(true);
        };

        const save = async () => {
            setSaving(true);
            try {
                const payload = { startDate: date, description };
                if (editingId) {
                    await store.editEvent(editingId, payload);
                } else {
                    await store.addEvent(payload);
                }
                setOpen(false);
            } finally {
                setSaving(false);
            }
        };

        useEffect(() => {
            const columns: IColDef[] = [
                {
                    headerName: "Date",
                    field: "startDate",
                    width: 160,
                    renderCell: (params: GridRenderCellParams) =>
                        formatShortDate(params.value),
                },
                { headerName: "Event", field: "description", flex: 1 },
                ...(canEdit
                    ? [
                          {
                              headerName: "",
                              field: "id",
                              width: 96,
                              sortable: false,
                              filterable: false,
                              align: "right" as const,
                              renderCell: (params: GridRenderCellParams) => (
                                  <Box sx={{ display: "flex", gap: 0.5 }}>
                                      <IconButton
                                          size="small"
                                          onClick={() =>
                                              openEdit(
                                                  params.row as ProgramEvent,
                                              )
                                          }
                                      >
                                          <EditOutlined fontSize="small" />
                                      </IconButton>
                                      <IconButton
                                          size="small"
                                          onClick={() =>
                                              store.deleteEvent(params.row.id)
                                          }
                                      >
                                          <DeleteOutline fontSize="small" />
                                      </IconButton>
                                  </Box>
                              ),
                          },
                      ]
                    : []),
            ];
            dgStore.checkboxSelection = false;
            dgStore.disableLocalConfig = true;
            dgStore.pagination = false;
            dgStore.hideFooterPagination = true;
            dgStore.hideHeader = true;
            dgStore.setColumns(columns);
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [canEdit, dgStore, store]);

        useEffect(() => {
            dgStore.setRows(store.organizationContext.programEvents);
            dgStore.isLoading = store.isLoading;
            dgStore.heightOverride = gridHeight(
                store.organizationContext.programEvents.length,
            );
        }, [dgStore, store.organizationContext.programEvents, store.isLoading]);

        return (
            <ClientBrainSectionCard
                title="Program Events"
                subtitle="Log changes and external events that have taken place so KBo can contextualize trends within the time period they occurred."
                headerAction={
                    canEdit ? (
                        <AcxButton
                            color="primary"
                            variant="text"
                            onClick={openAdd}
                            disabled={store.isLoading}
                        >
                            Add event
                        </AcxButton>
                    ) : undefined
                }
            >
                <AcxDataGrid dataGridStore={dgStore} />

                <Dialog
                    open={open}
                    onClose={() => setOpen(false)}
                    fullWidth
                    maxWidth="sm"
                >
                    <DialogTitle>
                        {editingId ? "Edit event" : "Add event"}
                    </DialogTitle>
                    <DialogContent
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                            pt: 1,
                        }}
                    >
                        <TextField
                            type="date"
                            label="Date"
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                        />
                        <AcxMainTextField
                            id="program-event-description"
                            labelText="Event"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            multiline
                            rows={3}
                            fullWidth
                        />
                    </DialogContent>
                    <DialogActions>
                        <AcxButton
                            color="white"
                            onClick={() => setOpen(false)}
                            disabled={saving}
                        >
                            Cancel
                        </AcxButton>
                        <AcxButton
                            color="primary"
                            onClick={save}
                            loading={saving}
                            disabled={!date || !description.trim()}
                        >
                            Save
                        </AcxButton>
                    </DialogActions>
                </Dialog>
            </ClientBrainSectionCard>
        );
    },
);

export default ProgramEventsSection;
